import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu as MenuIcon, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import Logo from "./Logo";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/about", label: "About Shay" },
  { to: "/reviews", label: "Reviews" },
  { to: "/track", label: "Track Order" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { count, openCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const transparent = isHome && !scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent ? "bg-transparent" : "bg-shay-cream/95 backdrop-blur-md border-b border-shay-gold/20"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="nav-logo-link" className="shrink-0">
          <Logo variant="badge" size="nav" src="/brand/Shay-cooks-logo-1.png" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[0.78rem] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ${
                  transparent
                    ? "text-shay-cream/90 hover:text-shay-gold"
                    : "text-shay-navy hover:text-shay-gold"
                } ${isActive ? "text-shay-gold" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-px bg-shay-gold transition-all duration-300 ${
                      isActive ? "w-6" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="open-cart-button"
            onClick={openCart}
            className={`relative h-10 w-10 grid place-items-center rounded-full transition-colors ${
              transparent
                ? "text-shay-cream hover:text-shay-gold"
                : "text-shay-navy hover:text-shay-gold"
            }`}
            aria-label="Open cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center text-[10px] font-semibold rounded-full bg-shay-gold text-shay-navy"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden h-10 w-10 grid place-items-center rounded-full ${
              transparent ? "text-shay-cream" : "text-shay-navy"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-out ${
          open ? "max-h-[600px]" : "max-h-0"
        } bg-shay-navy`}
      >
        <nav className="px-6 py-8 flex flex-col gap-1" data-testid="mobile-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-display text-3xl py-2 ${
                  isActive ? "text-shay-gold" : "text-shay-cream"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link to="/checkout" data-testid="mobile-order-link" className="btn-gold mt-6 self-start">
            Order Now
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
